/**
 * Normalizes a Google Ads customer ID for use in URLs and API calls.
 *
 * Google Ads customer IDs can be provided in various formats:
 * - With "customers/" prefix: "customers/1234567890"
 * - With dashes: "123-456-7890"
 * - Already normalized: "1234567890"
 *
 * This function normalizes all formats to the numeric format expected by
 * Google Ads URLs and API calls (e.g., "1234567890").
 *
 * @param customerId - The customer ID to normalize
 * @returns The normalized customer ID (numeric string without dashes or prefix)
 * @throws Error if the customer ID is invalid or cannot be normalized
 */
export function normalizeGoogleAdsCustomerId(customerId: string | undefined | null): string {
  if (!customerId || typeof customerId !== 'string') {
    throw new Error('Customer ID must be a non-empty string')
  }

  // Remove "customers/" prefix if present
  let normalizedId = customerId.replace(/^customers\//, '')

  // Remove all dashes (handles xxx-xxx-xxx format)
  normalizedId = normalizedId.replace(/-/g, '')

  // Validate that the result is numeric
  if (!/^\d+$/.test(normalizedId)) {
    throw new Error(
      `Invalid customer ID format: ${customerId}. Must be numeric or in xxx-xxx-xxx format`
    )
  }

  return normalizedId
}
